import * as React from 'react';
import { AccountGroup } from '@amzn/aac-chrome-internal-api';
import {
  fetchAccountsPage,
  AccountPageResponse,
} from 'src/commons/apis/browse-accounts-switcher';
import { mergeAccountGroups } from './accountHelper';
import { logWithTakt } from 'src/commons/utils/logWithTakt';
import { AccountSwitcherPublisher } from 'src/commons/utils/metrics';
import { Cohort, publishCohortCounter } from 'src/commons/utils/whaleCohort';

/** Local infinite-scroll page size (non-whale reveals from the in-memory array). */
export const LOCAL_PAGE_SIZE = 100;
/** Debounce for whale server-side search. */
export const SEARCH_DEBOUNCE_MS = 300;
/** Minimum characters before a search (local or server) is applied. */
export const MIN_SEARCH_CHARS = 2;

/**
 * The single data-source abstraction consumed by the account switcher UI.
 * Components render `accounts` and call `loadMore`/`search`/`clearSearch`
 * without knowing whether the source is a local array (non-whale) or a
 * cursor-paged server (whale). Whale vs non-whale is decided once, from the
 * `isPrecomputed` flag seeded at page load.
 */
export interface AccountDataSource {
  accounts: AccountGroup[];
  cohort: Cohort;
  totalCount: number;
  hasMore: boolean;
  loadMore: () => void;
  search: (query: string) => void;
  clearSearch: () => void;
  isLoading: boolean;
  error: Error | null;
  /** Re-attempt the last failed page fetch (clears the error, re-fires it). */
  retry: () => void;
}

export interface UseAccountDataParams {
  /**
   * Whale flag — routes to the server-paged source when true, local otherwise.
   * Derived by the provider from `accountswitcher-init.nextToken` (a non-null
   * cursor means more pages exist server-side); `navbar-init.isPrecomputed` is
   * not used for this decision.
   */
  isPrecomputed: boolean;
  /** Grouped accounts seeded from `/accountswitcher-init` page load. */
  seedAccounts: AccountGroup[];
  /** Whole-inventory count from `/navbar-init.accountCount` (telemetry). */
  seedTotalCount: number;
  /** Opaque page-one pagination token (whale only); null on non-whale. */
  seedNextToken: string | null;
  /** True when the `/accountswitcher-init` seed load itself failed. */
  seedFailed?: boolean;
  /** Nav app the switcher belongs to; forwarded to the server as `appName`. */
  appName?: string;
  /**
   * Current-account marketplace id. Used when merging paged global accounts
   * to re-pick the global account's switch target the way Trim does.
   */
  currentMarketplaceId?: string;
}

const countAccounts = (groups: AccountGroup[]): number =>
  groups.reduce((sum, group) => sum + group.accounts.length, 0);

const localFilter = (groups: AccountGroup[], query: string): AccountGroup[] => {
  // Case-insensitive substring match. NOT a RegExp built from the query —
  // raw user input like "[[" is an invalid pattern that throws during render,
  // and pathological patterns can hang the UI. Plain substring is safe.
  const needle = query.toLowerCase();
  return groups
    .map((group) => ({
      ...group,
      accounts: group.accounts.filter((account) => {
        const combined = [
          account.label,
          account.entityId,
          account.marketplaceName,
        ]
          .join('|')
          .toLowerCase();
        return combined.includes(needle);
      }),
    }))
    .filter((group) => group.accounts.length > 0);
};

/**
 * Non-whale data source: everything is already in memory from
 * `/accountswitcher-init`. Search is an instant case-insensitive substring; `loadMore`
 * reveals the next local chunk. Never enters a loading state after page load.
 */
const useLocalAccountData = (
  allAccounts: AccountGroup[],
  totalCount: number,
): AccountDataSource => {
  const [query, setQuery] = React.useState('');
  const [visibleCount, setVisibleCount] = React.useState(LOCAL_PAGE_SIZE);

  const filtered = React.useMemo(() => {
    if (query.length < MIN_SEARCH_CHARS) {
      return allAccounts;
    }
    return localFilter(allAccounts, query);
  }, [allAccounts, query]);

  const filteredTotal = React.useMemo(
    () => countAccounts(filtered),
    [filtered],
  );

  // Restart the window whenever the visible set changes (new search / cleared).
  React.useEffect(() => {
    setVisibleCount(LOCAL_PAGE_SIZE);
  }, [filtered]);

  // Window the flattened account sequence while preserving group structure.
  const windowed = React.useMemo(() => {
    let remaining = visibleCount;
    const result: AccountGroup[] = [];
    for (const group of filtered) {
      if (remaining <= 0) {
        break;
      }
      const slice = group.accounts.slice(0, remaining);
      remaining -= slice.length;
      result.push({ ...group, accounts: slice });
    }
    return result;
  }, [filtered, visibleCount]);

  const search = React.useCallback((next: string) => {
    setQuery(next);
    if (next.length >= MIN_SEARCH_CHARS) {
      publishCohortCounter(AccountSwitcherPublisher, 'Search', 'NonWhale');
      logWithTakt('ccx-account-switcher-search', {
        source: 'local',
        isPrecomputed: false,
        queryLength: next.length,
        cohort: 'NonWhale',
      });
    }
  }, []);
  const clearSearch = React.useCallback(() => setQuery(''), []);
  const loadMore = React.useCallback(() => {
    logWithTakt('ccx-account-switcher-scroll-load', { source: 'local' });
    setVisibleCount((prev) => Math.min(prev + LOCAL_PAGE_SIZE, filteredTotal));
  }, [filteredTotal]);

  return {
    accounts: windowed,
    cohort: 'NonWhale',
    totalCount,
    hasMore: visibleCount < filteredTotal,
    loadMore,
    search,
    clearSearch,
    isLoading: false,
    error: null,
    // Local data never errors (everything is in memory) — nothing to retry.
    retry: () => undefined,
  };
};

/**
 * Whale data source: page 1 arrives via `/accountswitcher-init` (seeded), and
 * further pages / searches hit `POST /trim/accounts` (browse = no query, search
 * = query, paginate = nextToken). The token is opaque — echoed verbatim, never
 * parsed. In-flight requests are de-duplicated so rapid scrolling never launches
 * parallel page fetches, stale responses are dropped, and the last successful
 * data is retained when a request errors.
 */
const useServerAccountData = (
  seedAccounts: AccountGroup[],
  seedTotalCount: number,
  seedNextToken: string | null,
  seedFailed: boolean,
  appName?: string,
  currentMarketplaceId?: string,
): AccountDataSource => {
  const [accounts, setAccounts] = React.useState<AccountGroup[]>(seedAccounts);
  const [totalCount, setTotalCount] = React.useState(seedTotalCount);
  const [nextToken, setNextToken] = React.useState<string | null>(
    seedNextToken,
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(
    seedFailed ? new Error('account seed load failed') : null,
  );

  // requestId is the single source of truth for "latest wins": every fetch
  // takes a ticket, and only the most recent ticket's response is applied — so a
  // newer search/clear supersedes a slower in-flight request instead of being
  // dropped. paginating is a *synchronous* guard so rapid scroll can't fire the
  // same next-page request twice before isLoading re-renders.
  const requestId = React.useRef(0);
  const paginationSequence = React.useRef(0);
  const activePagination = React.useRef<number | null>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();
  // Holds the current request's controller so a newer fetch can abort the
  // in-flight one on the wire (latest-wins already drops its response; this
  // stops the wasted work and frees the connection).
  const abortRef = React.useRef<AbortController | null>(null);
  // Tracks whether a server search is currently applied, so deleting the query
  // back below the min-char gate restores the browse view (rather than leaving
  // stale search results on screen).
  const hasActiveSearch = React.useRef(false);
  // The last fetch's args + append mode, so `retry` can replay exactly the
  // request that failed (a paginate keeps append=true; a browse/search resets).
  const lastFetchRef = React.useRef<{
    params: { query?: string; nextToken?: string | null };
    append: boolean;
  } | null>(null);

  // The seed props (page-1 accounts/token/count) arrive asynchronously from
  // `/accountswitcher-init` and can land AFTER this hook mounts (mobile mounts
  // before detailed loading finishes). useState only reads its initializer once,
  // so re-sync from later seed changes here — but only while no fetch has run
  // yet (requestId 0), so a user-driven paginate/search is never clobbered.
  //
  // The parent may pass a fresh `seedAccounts` array identity on renders where
  // the content is unchanged, so guard on the seeds' actual VALUES (not identity)
  // — else this setState-in-effect re-renders forever. `applied` remembers the
  // last values we synced so an unchanged re-render is a no-op.
  const appliedSeed = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (requestId.current !== 0) {
      return;
    }
    const signature = JSON.stringify({
      ids: seedAccounts.map((g) => g.accounts.map((a) => a.entityId)),
      seedTotalCount,
      seedNextToken,
      seedFailed,
    });
    if (appliedSeed.current === signature) {
      return;
    }
    appliedSeed.current = signature;
    setAccounts(seedAccounts);
    setTotalCount(seedTotalCount);
    setNextToken(seedNextToken);
    setError(seedFailed ? new Error('account seed load failed') : null);
  }, [seedAccounts, seedTotalCount, seedNextToken, seedFailed]);

  const runFetch = React.useCallback(
    async (
      params: { query?: string; nextToken?: string | null },
      { append }: { append: boolean },
    ) => {
      if (!append) {
        // A search/browse request supersedes any page request immediately. The
        // old request may settle later, so its token must not block a new page.
        activePagination.current = null;
      }
      lastFetchRef.current = { params, append };
      const id = (requestId.current += 1);
      // Cancel any request this one supersedes, then arm a fresh controller.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);
      setError(null);
      try {
        const page: AccountPageResponse = await fetchAccountsPage(
          { ...params, appName },
          controller.signal,
        );
        // Drop a response superseded by a newer request (latest wins).
        if (id !== requestId.current || controller.signal.aborted) {
          return;
        }

        setAccounts((previousAccounts) =>
          append
            ? mergeAccountGroups(
                previousAccounts,
                page.accounts,
                currentMarketplaceId,
              )
            : page.accounts,
        );
        setTotalCount(page.totalCount);
        setNextToken(page.nextToken);
      } catch (e) {
        // An intentional abort (supersede / unmount) is not a failure — never
        // surface it. Otherwise retain last-good data and expose the error.
        if (id === requestId.current && !controller.signal.aborted) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (id === requestId.current && !controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [appName, currentMarketplaceId],
  );

  const loadMore = React.useCallback(() => {
    if (!nextToken || activePagination.current !== null) {
      return;
    }
    const paginationId = (paginationSequence.current += 1);
    activePagination.current = paginationId;
    logWithTakt('ccx-account-switcher-scroll-load', { source: 'server' });
    // The token already carries the active query, so paginate with token only.
    void runFetch({ nextToken }, { append: true }).finally(() => {
      if (activePagination.current === paginationId) {
        activePagination.current = null;
      }
    });
  }, [nextToken, runFetch]);

  const search = React.useCallback(
    (next: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (next.length < MIN_SEARCH_CHARS) {
        // Deleting an applied search back below the gate restores browse.
        if (hasActiveSearch.current) {
          hasActiveSearch.current = false;
          void runFetch({}, { append: false });
        }
        return;
      }
      publishCohortCounter(AccountSwitcherPublisher, 'Search', 'Whale');
      debounceRef.current = setTimeout(() => {
        hasActiveSearch.current = true;
        logWithTakt('ccx-account-switcher-search', {
          source: 'server',
          isPrecomputed: true,
          queryLength: next.length,
          cohort: 'Whale',
        });
        void runFetch({ query: next }, { append: false });
      }, SEARCH_DEBOUNCE_MS);
    },
    [runFetch],
  );

  const clearSearch = React.useCallback(() => {
    hasActiveSearch.current = false;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // Restore the browse view from page 1 (no query, no token).
    void runFetch({}, { append: false });
  }, [runFetch]);

  const retry = React.useCallback(() => {
    const last = lastFetchRef.current;
    if (!last) {
      return;
    }
    logWithTakt('ccx-account-switcher-retry', { source: 'server' });
    void runFetch(last.params, { append: last.append });
  }, [runFetch]);

  React.useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      abortRef.current?.abort();
    },
    [],
  );

  return {
    accounts,
    cohort: 'Whale',
    totalCount,
    hasMore: nextToken !== null,
    loadMore,
    search,
    clearSearch,
    isLoading,
    error,
    retry,
  };
};

/**
 * Encapsulates account-switcher data sourcing. Whale vs non-whale routing is
 * decided once here (from `isPrecomputed` seeded at page load); callers get one
 * uniform interface. Both internal hooks are called unconditionally (rules of
 * hooks) and one result is selected — `isPrecomputed` does not change within a
 * mounted dropdown.
 *
 * The backend resolves the nav app from the request Referer on every
 * browse/search call; `appName` is forwarded only as an explicit override.
 */
export const useAccountData = ({
  isPrecomputed,
  seedAccounts,
  seedTotalCount,
  seedNextToken,
  seedFailed = false,
  appName,
  currentMarketplaceId,
}: UseAccountDataParams): AccountDataSource => {
  const local = useLocalAccountData(seedAccounts, seedTotalCount);
  const server = useServerAccountData(
    seedAccounts,
    seedTotalCount,
    seedNextToken,
    seedFailed,
    appName,
    currentMarketplaceId,
  );

  return isPrecomputed ? server : local;
};
